import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useEffect, useMemo, useState } from 'react'
import { baseUrl } from '../../../../services/baseUrl'
import { constraintConfig } from '../../../../constraintConfig'

type UserSummary = {
    orgId?: number
    orgUserId?: number
    firstName?: string
    lastName?: string
    fullName?: string
    email?: string
    employeeId?: string
    cellPhone?: string
    businessPhone?: string
    divisionName?: string
    departmentName?: string
    designationName?: string
    locationName?: string
    shipToAddress?: string
    spendingLimit?: number | null
    spendingLimitDisplay?: string
    status?: string
    creditCardRequestStatusId?: number | null
    ccRequestedDate?: string | null
}

const DetailRow = ({ label, value }: { label: string; value?: string | number | null }) => (
    <div className='d-flex justify-content-between align-items-start py-2 border-bottom border-gray-200'>
        <span className='text-muted fw-semibold me-3'>{label}</span>
        <span className='text-gray-800 text-end'>{value != null && value !== '' ? value : 'N/A'}</span>
    </div>
)

const ApproveCCCardRequest = () => {
    const { token } = useParams()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isError, setIsError] = useState(false)
    const [isAlreadyProcessed, setIsAlreadyProcessed] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [resultMessage, setResultMessage] = useState('')
    const [user, setUser] = useState<UserSummary | null>(null)
    const [processingStep, setProcessingStep] = useState('Preparing request…')

    const tokenParts = token?.split('_') || []
    const actionId = tokenParts.length >= 3 ? Number(tokenParts[2]) : NaN
    const isApprove = actionId === constraintConfig.ccCardRequestStatus.RequestApproved
    const actionLabel = isApprove ? 'Approve' : 'Reject'
    const actionVerb = isApprove ? 'Approving' : 'Rejecting'

    const displayName = useMemo(() => {
        if (!user) return ''
        return user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }, [user])

    useEffect(() => {
        const processRequest = async () => {
            const parts = token?.split('_') || []
            const action = parts.length >= 3 ? Number(parts[2]) : NaN
            if (!token || parts.length < 4 || Number.isNaN(action)) {
                toast.error('Invalid approval token')
                setIsError(true)
                setErrorMessage('Missing or invalid approval token. The link may be expired or invalid.')
                setIsLoading(false)
                return
            }

            const approving = action === constraintConfig.ccCardRequestStatus.RequestApproved
            const encoded = encodeURIComponent(token)

            try {
                setIsLoading(true)
                setProcessingStep('Loading user request details…')

                // Preview: load user info first so processing message shows who is being acted on
                try {
                    const preview = await axios.get(
                        `${baseUrl.MAIN_BASE_URL}/api/Account/GetCCCardRequestActionPreview?request=${encoded}`,
                        { headers: { accept: '*/*' } }
                    )
                    if (preview.data?.success && preview.data?.result?.user) {
                        setUser(preview.data.result.user)
                        const name =
                            preview.data.result.user.fullName ||
                            `${preview.data.result.user.firstName || ''} ${preview.data.result.user.lastName || ''}`.trim()
                        setProcessingStep(
                            approving
                                ? `Approving corporate credit card request for ${name || 'this user'}…`
                                : `Rejecting corporate credit card request for ${name || 'this user'}…`
                        )
                    } else {
                        setProcessingStep(
                            approving
                                ? 'Approving corporate credit card request…'
                                : 'Rejecting corporate credit card request…'
                        )
                    }
                } catch {
                    setProcessingStep(
                        approving
                            ? 'Approving corporate credit card request…'
                            : 'Rejecting corporate credit card request…'
                    )
                }

                const loadingToast = toast.loading(
                    approving
                        ? 'Approving credit card request... Please wait.'
                        : 'Rejecting credit card request... Please wait.'
                )

                const response = await axios.post(
                    `${baseUrl.MAIN_BASE_URL}/api/Account/RequestCCCardAction?request=${encoded}`,
                    {},
                    {
                        headers: {
                            accept: '*/*',
                            'Content-Type': 'application/json',
                        },
                    }
                )

                toast.dismiss(loadingToast)
                if (response.data && response.data.success) {
                    toast.success(response.data?.message)
                    setResultMessage(response.data?.message || 'Request processed successfully.')
                    if (response.data?.result?.user) {
                        setUser(response.data.result.user)
                    }
                    setIsSuccess(true)
                } else {
                    const msg = response.data?.message || 'Unable to process request.'
                    const already =
                        response.data?.result?.alreadyProcessed === true ||
                        response.data?.result?.status === 'AlreadyApproved' ||
                        response.data?.result?.status === 'AlreadyRejected' ||
                        /already (approved|rejected|processed)/i.test(msg)
                    if (response.data?.result?.user) {
                        setUser(response.data.result.user)
                    }
                    if (already) {
                        setIsAlreadyProcessed(true)
                        setResultMessage(msg)
                        toast.info(msg)
                    } else {
                        toast.error(msg)
                        setIsError(true)
                        setErrorMessage(msg)
                    }
                }
            } catch (error: any) {
                toast.dismiss()
                const errorMsg = error?.response?.data?.message || 'Unable to process credit card request.'
                const already =
                    error?.response?.data?.result?.alreadyProcessed === true ||
                    error?.response?.data?.result?.status === 'AlreadyApproved' ||
                    error?.response?.data?.result?.status === 'AlreadyRejected' ||
                    /already (approved|rejected|processed)/i.test(errorMsg)
                if (error?.response?.data?.result?.user) {
                    setUser(error.response.data.result.user)
                }
                if (already) {
                    setIsAlreadyProcessed(true)
                    setResultMessage(errorMsg)
                    toast.info(errorMsg)
                } else {
                    toast.error(errorMsg)
                    setIsError(true)
                    setErrorMessage(errorMsg)
                }
            } finally {
                setIsLoading(false)
            }
        }

        processRequest()
    }, [token, navigate])

    const UserDetailsCard = () => {
        if (!user) return null
        return (
            <div className='card card-dashed bg-light mb-6'>
                <div className='card-body p-5'>
                    <div className='d-flex align-items-center mb-4'>
                        <div className='symbol symbol-circle symbol-50px overflow-hidden me-3'>
                            <div className='symbol-label fs-3 bg-light-info text-info text-capitalize'>
                                {(displayName || user.email || '?').charAt(0)}
                            </div>
                        </div>
                        <div>
                            <div className='fw-bold text-gray-800 text-capitalize fs-5'>{displayName || 'N/A'}</div>
                            <div className='text-muted text-lowercase'>{user.email || 'N/A'}</div>
                        </div>
                    </div>
                    <h6 className='fw-bold text-gray-700 mb-3'>User Basic Details</h6>
                    <DetailRow label='Employee ID' value={user.employeeId} />
                    <DetailRow label='Cell Phone' value={user.cellPhone} />
                    <DetailRow label='Business Phone' value={user.businessPhone} />
                    <DetailRow label='Division' value={user.divisionName} />
                    <DetailRow label='Department' value={user.departmentName} />
                    <DetailRow label='Designation' value={user.designationName} />
                    <DetailRow label='Office Location' value={user.locationName} />
                    <DetailRow label='Credit Card Limit' value={user.spendingLimitDisplay || user.spendingLimit} />
                    <DetailRow label='Ship To Address' value={user.shipToAddress} />
                    {user.status && <DetailRow label='Request Status' value={user.status} />}
                </div>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className='d-flex justify-content-center align-items-center min-vh-50 p-4'>
                <div className='card shadow-sm border-0' style={{ maxWidth: '560px', width: '100%' }}>
                    <div className='card-body p-6 text-center'>
                        <div className='spinner-border text-primary mb-5' style={{ width: '3rem', height: '3rem' }} />
                        <h3 className='fw-bold mb-3'>{actionVerb} Credit Card Request</h3>
                        <p className='text-gray-700 fs-5 mb-2'>{processingStep}</p>
                        {displayName && (
                            <p className='text-muted mb-4'>
                                User: <span className='fw-semibold text-gray-800 text-capitalize'>{displayName}</span>
                                {user?.email ? (
                                    <>
                                        {' '}
                                        (<span className='text-lowercase'>{user.email}</span>)
                                    </>
                                ) : null}
                            </p>
                        )}
                        <p className='text-muted fs-7 mb-0'>Please wait — do not close this page.</p>
                        {user && (
                            <div className='mt-6 text-start'>
                                <UserDetailsCard />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (isAlreadyProcessed) {
        const isAlreadyApproved = /already approved/i.test(resultMessage)
        return (
            <div className='d-flex justify-content-center align-items-center min-vh-50 p-4'>
                <div className='card shadow-sm border-0' style={{ maxWidth: '560px', width: '100%' }}>
                    <div className='card-body p-6'>
                        <div className='text-center mb-5'>
                            <span className={`badge badge-lg mb-4 ${isAlreadyApproved ? 'badge-warning' : 'badge-light-warning'}`}>
                                Already Processed
                            </span>
                            <h2 className='text-warning mb-3 fw-bold'>
                                {isAlreadyApproved ? 'Already Approved' : 'Already Processed'}
                            </h2>
                            <p className='fs-5 text-gray-700'>{resultMessage}</p>
                        </div>
                        <UserDetailsCard />
                        <div className='d-flex justify-content-center gap-3'>
                            <button className='btn btn-primary btn-sm px-4' onClick={() => navigate('/auth/login')}>
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div className='d-flex justify-content-center align-items-center min-vh-50 p-4'>
                <div className='card shadow-sm border-0' style={{ maxWidth: '560px', width: '100%' }}>
                    <div className='card-body p-6'>
                        <div className='text-center mb-5'>
                            <span className='badge badge-lg badge-danger mb-4'>Failure</span>
                            <h2 className='text-danger mb-3 fw-bold'>{actionLabel} Failed</h2>
                            <p className='fs-5 text-gray-700'>{errorMessage}</p>
                        </div>
                        <UserDetailsCard />
                        <div className='d-flex justify-content-center gap-3'>
                            <button className='btn btn-primary btn-sm px-4' onClick={() => window.location.reload()}>
                                Try Again
                            </button>
                            <button className='btn btn-light btn-sm px-4' onClick={() => navigate('/auth/login')}>
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (isSuccess) {
        return (
            <div className='d-flex justify-content-center align-items-center min-vh-50 p-4'>
                <div className='card shadow-sm border-0' style={{ maxWidth: '560px', width: '100%' }}>
                    <div className='card-body p-6'>
                        <div className='text-center mb-5'>
                            <span className={`badge badge-lg mb-4 ${isApprove ? 'badge-success' : 'badge-warning'}`}>
                                Success
                            </span>
                            <h2 className={`${isApprove ? 'text-success' : 'text-warning'} mb-3 fw-boldest`}>
                                Credit Card Request {isApprove ? 'Approved' : 'Rejected'}
                            </h2>
                            <p className='fs-5 text-gray-700 mb-0'>{resultMessage}</p>
                            {displayName && (
                                <p className='text-muted mt-3 mb-0'>
                                    Confirmed for{' '}
                                    <span className='fw-semibold text-gray-800 text-capitalize'>{displayName}</span>
                                </p>
                            )}
                        </div>

                        <UserDetailsCard />

                        {isApprove && (
                            <div className='alert alert-primary py-3 mb-6'>
                                The ordering team has been notified to proceed with card issuance for this employee.
                            </div>
                        )}

                        <div className='d-flex flex-column gap-3'>
                            <button
                                className='btn btn-primary btn-lg fw-bold py-3'
                                onClick={() => navigate('/auth/login')}
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return null
}

export default ApproveCCCardRequest
